import { Link } from '@tanstack/react-router';
import { ArrowRight, Banknote, BarChart3, Boxes, Factory, ScanLine, ShieldCheck, Users } from 'lucide-react';
import { MarketingShell } from './MarketingShell';

const GROUPS = [
  {
    icon: Boxes,
    title: 'Point of sale',
    items: [
      'Fast sell screen for counter, table, or field',
      'Barcode scanning and label printing',
      'Receipt printers, cash drawers, and weighing scales',
      'Discounts, returns, holds, and split payments',
    ],
  },
  {
    icon: ScanLine,
    title: 'Inventory',
    items: [
      'Real-time stock across multiple locations',
      'FIFO cost and lot / batch tracking',
      'Purchase orders and supplier management',
      'Low-stock alerts and stock-take counts',
    ],
  },
  {
    icon: Users,
    title: 'People & customers',
    items: [
      'Staff, roles, shifts, attendance, and leave',
      'Payroll with allowances and deductions',
      'Customer and supplier ledgers with dues',
      'CRM follow-ups, leads, and proposals',
    ],
  },
  {
    icon: Banknote,
    title: 'Money',
    items: [
      'BDT-native invoicing and receipts',
      'SSLCommerz online payments + cash collection',
      'Expenses, accounts, and cash registers',
      'VAT-ready tax handling',
    ],
  },
  {
    icon: Factory,
    title: 'Make & repair',
    items: [
      'Manufacturing recipes and production runs',
      'Ingredient and raw-material tracking',
      'Repair job sheets and device history',
      'Service status from intake to delivery',
    ],
  },
  {
    icon: BarChart3,
    title: 'Insight & control',
    items: [
      'Daily sales, profit & loss, and stock reports',
      'Audit log of who did what, and when',
      'Two-factor authentication and device sessions',
      'WooCommerce sync for your online store',
    ],
  },
];

export function Features() {
  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10 text-center">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">Features</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto leading-[1.05]">
          Everything a growing business runs on.
        </h1>
        <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          Possignin is six tools in one — so your counter, your stockroom, your staff, and your
          accounts all speak the same language.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-6">
            <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle dark:bg-brand/15 text-brand dark:text-brand-dm">
              <g.icon className="w-5 h-5" />
            </span>
            <h2 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{g.title}</h2>
            <ul className="mt-3 space-y-2">
              {g.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand/60 shrink-0" /> {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/40 p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <ShieldCheck className="w-8 h-8 text-brand dark:text-brand-dm shrink-0" />
            <p className="text-zinc-700 dark:text-zinc-200">
              Your data stays in <span className="font-medium">your own database</span> — exportable any time, never sold.
            </p>
          </div>
          <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition whitespace-nowrap">
            Start free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
